import React from 'react';
import { fmtMoney, fmtDate, taxRateToPercent, previewItems } from './format';

const SIDEBAR = '#0f172a';
const DEFAULT_ACCENT = '#22c55e';
const INK = '#1e293b';

/**
 * Modèle "Moderne" — sidebar sombre verticale (logo + coordonnées),
 * zone principale épurée, table sans bordures et carte de total mise en avant.
 * Couleur d'accent personnalisable via branding.brandColor.
 */
export default function ModernTemplate({ invoice = {}, branding = {} }) {
  const accent = branding.brandColor || DEFAULT_ACCENT;
  const currency = invoice.currency || 'USD';
  const items = previewItems(invoice.items);

  const companyName = branding.companyName || 'Votre entreprise';
  const initials = (companyName.trim()[0] || 'F').toUpperCase();

  const sideLabel = { fontSize: 9, letterSpacing: 1, textTransform: 'uppercase', color: 'rgba(255,255,255,0.45)', marginBottom: 3 };
  const sideValue = { fontSize: 11, color: 'rgba(255,255,255,0.9)', lineHeight: 1.5, wordBreak: 'break-word' };
  const th = { fontSize: 10, letterSpacing: 0.5, textTransform: 'uppercase', color: '#94a3b8', padding: '0 0 8px', textAlign: 'left' };
  const td = { fontSize: 13, color: INK, padding: '12px 0', borderBottom: '1px solid #eef2f7' };

  return (
    <div style={{ width: 720, display: 'flex', background: '#fff', color: INK, fontFamily: 'Arial, Helvetica, sans-serif', minHeight: 520 }}>
      {/* Sidebar sombre */}
      <div style={{ width: 220, background: SIDEBAR, color: '#fff', padding: '32px 24px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
        <div>
          <div style={{ width: 56, height: 56, borderRadius: 14, background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', marginBottom: 18 }}>
            {branding.logoUrl ? (
              <img src={branding.logoUrl} alt="logo" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              <span style={{ color: SIDEBAR, fontWeight: 800, fontSize: 24 }}>{initials}</span>
            )}
          </div>
          <div style={{ fontSize: 17, fontWeight: 800, lineHeight: 1.2 }}>{companyName}</div>
          {branding.tagline && <div style={{ fontSize: 10, color: accent, marginTop: 4 }}>{branding.tagline}</div>}

          <div style={{ height: 3, width: 40, background: accent, borderRadius: 2, margin: '20px 0' }} />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <div style={sideLabel}>Téléphone</div>
            <div style={sideValue}>{branding.companyPhone || '+0 000 000 000'}</div>
          </div>
          <div>
            <div style={sideLabel}>Email</div>
            <div style={sideValue}>{branding.companyEmail || 'contact@entreprise.com'}</div>
          </div>
          <div>
            <div style={sideLabel}>Adresse</div>
            <div style={sideValue}>{branding.companyAddress || 'Adresse de l\'entreprise'}</div>
          </div>
        </div>
      </div>

      {/* Zone principale */}
      <div style={{ flex: 1, padding: '32px 32px 28px', display: 'flex', flexDirection: 'column' }}>
        {/* Titre + méta */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 26 }}>
          <div>
            <div style={{ fontSize: 34, fontWeight: 800, color: INK, letterSpacing: 1 }}>FACTURE</div>
            <div style={{ fontSize: 13, color: accent, fontWeight: 700, marginTop: 2 }}>{invoice.invoiceNumber || 'Brouillon'}</div>
          </div>
          <div style={{ textAlign: 'right', fontSize: 11, color: '#64748b', lineHeight: 1.7 }}>
            <div><span style={{ color: '#94a3b8' }}>Émise&nbsp;:</span> {fmtDate(invoice.issueDate || invoice.createdAt || Date.now())}</div>
            <div><span style={{ color: '#94a3b8' }}>Échéance&nbsp;:</span> {fmtDate(invoice.dueDate)}</div>
          </div>
        </div>

        {/* Facturé à */}
        <div style={{ marginBottom: 22 }}>
          <div style={{ fontSize: 10, letterSpacing: 1, textTransform: 'uppercase', color: '#94a3b8', marginBottom: 6 }}>Facturé à</div>
          <div style={{ fontSize: 16, fontWeight: 800, color: INK }}>{invoice.clientName || '—'}</div>
          <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
            {[invoice.clientEmail, invoice.clientPhone].filter(Boolean).join('  ·  ') || '—'}
          </div>
        </div>

        {/* Table */}
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={th}>Description</th>
              <th style={{ ...th, textAlign: 'center', width: 50 }}>Qté</th>
              <th style={{ ...th, textAlign: 'right', width: 100 }}>Prix</th>
              <th style={{ ...th, textAlign: 'right', width: 110 }}>Total</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan={4} style={{ ...td, textAlign: 'center', color: '#94a3b8', fontStyle: 'italic' }}>Aucune ligne pour le moment</td></tr>
            ) : (
              items.map((it, i) => (
                <tr key={i}>
                  <td style={td}>{it.description || '—'}</td>
                  <td style={{ ...td, textAlign: 'center' }}>{it.quantity}</td>
                  <td style={{ ...td, textAlign: 'right' }}>{fmtMoney(it.unitPrice, currency)}</td>
                  <td style={{ ...td, textAlign: 'right', fontWeight: 700 }}>{fmtMoney(it.total, currency)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {/* Totaux */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 18 }}>
          <div style={{ width: 250 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', fontSize: 12, color: '#64748b' }}>
              <span>Sous-total</span><span>{fmtMoney(invoice.subtotal, currency)}</span>
            </div>
            {Number(invoice.discount) > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', fontSize: 12, color: '#64748b' }}>
                <span>Remise</span><span>-{fmtMoney(invoice.discount, currency)}</span>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', fontSize: 12, color: '#64748b' }}>
              <span>TVA ({taxRateToPercent(invoice.taxRate)}%)</span><span>{fmtMoney(invoice.taxAmount, currency)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10, background: SIDEBAR, borderRadius: 10, padding: '12px 16px' }}>
              <span style={{ color: '#fff', fontSize: 13, fontWeight: 600 }}>Total</span>
              <span style={{ color: accent, fontSize: 18, fontWeight: 800 }}>{fmtMoney(invoice.total, currency)}</span>
            </div>
          </div>
        </div>

        {/* Notes */}
        {invoice.notes && (
          <div style={{ marginTop: 'auto', paddingTop: 22 }}>
            <div style={{ fontSize: 10, letterSpacing: 1, textTransform: 'uppercase', color: '#94a3b8', marginBottom: 5 }}>Notes</div>
            <div style={{ fontSize: 11, color: '#64748b', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{invoice.notes}</div>
          </div>
        )}
      </div>
    </div>
  );
}
